/**
 * MQTT Admin Connection Hook
 *
 * This hook provides a secure, role-based MQTT connection for admin operations
 * and manages the hybrid workflow between MQTT direct changes and backend API sync.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAgentAuthorization } from './useAgentAuthorization';
import mqttAdminService from '../services/mqtt/mqttAdminService';
import { toast } from 'react-toastify';

/**
 * Hook for managing MQTT admin connections and operations
 * @returns {Object} MQTT admin connection state and methods
 */
export const useMqttAdminConnection = () => {
  // Get user role and tenant from authorization hook
  const { userRole, userTenant } = useAgentAuthorization();

  // Connection state
  const [mqttConnection, setMqttConnection] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Pending changes that need to be synced to backend
  const [pendingChanges, setPendingChanges] = useState(new Map());

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  // User info for audit logging
  const userInfo = {
    userId: localStorage.getItem('userId') || 'unknown',
    userEmail: localStorage.getItem('userEmail') || 'unknown',
    userRole,
    userTenant,
  };

  /**
   * Initialize MQTT admin connection
   */
  const initializeConnection = useCallback(async () => {
    // Only admins and tenant admins can use MQTT admin features
    if (!['admin', 'tenant_admin'].includes(userRole)) {
      setConnectionError('Insufficient permissions for MQTT admin access');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const connection = await mqttAdminService.initializeMqttAdminConnection(userRole, userTenant);
      setMqttConnection(connection);
      // Silence success/warning to avoid repeated notifications; errors still toast below
    } catch (error) {
      console.error('Error initializing MQTT admin connection:', error);
      setConnectionError(error.message);
      toast.error(`MQTT connection failed: ${error.message}`);
    } finally {
      setIsConnecting(false);
    }
  }, [userRole, userTenant]);

  /**
   * Apply configuration changes to an agent via MQTT
   * @param {string} agentId - Agent ID
   * @param {Object} config - Configuration changes
   * @returns {Promise<Object>} Result with action ID and timestamp
   */
  const applyConfigToAgent = useCallback(
    async (agentId, config) => {
      if (!mqttConnection) {
        throw new Error('MQTT admin connection not initialized');
      }

      try {
        const result = await mqttAdminService.applyConfigToAgent(agentId, config, userInfo);

        // Add to pending changes if sync is needed
        if (result.needsSync) {
          setPendingChanges(prev => {
            const newMap = new Map(prev);
            newMap.set(result.actionId, {
              agentId,
              changes: config,
              actionId: result.actionId,
              timestamp: result.timestamp,
              type: 'config',
            });
            return newMap;
          });

          toast.info(
            'Changes applied to agent via MQTT. Click "Save to System" to persist changes.'
          );
        }

        // Update audit log
        refreshAuditLog();

        return result;
      } catch (error) {
        console.error(`Error applying config to agent ${agentId}:`, error);
        toast.error(`Failed to apply config: ${error.message}`);
        throw error;
      }
    },
    [mqttConnection, userInfo]
  );

  /**
   * Send a command to an agent via MQTT
   * @param {string} agentId - Agent ID
   * @param {string} action - Command action (restart, diagnostics, etc.)
   * @param {Object} parameters - Command parameters
   * @returns {Promise<Object>} Result with action ID and timestamp
   */
  const sendCommandToAgent = useCallback(
    async (agentId, action, parameters = {}) => {
      if (!mqttConnection) {
        throw new Error('MQTT admin connection not initialized');
      }

      try {
        const result = await mqttAdminService.sendCommandToAgent(
          agentId,
          action,
          parameters,
          userInfo
        );

        // Add to pending changes if sync is needed
        if (result.needsSync) {
          setPendingChanges(prev => {
            const newMap = new Map(prev);
            newMap.set(result.actionId, {
              agentId,
              changes: { action, parameters },
              actionId: result.actionId,
              timestamp: result.timestamp,
              type: 'command',
            });
            return newMap;
          });

          toast.info(
            `Command "${action}" sent to agent. Click "Save to System" to persist changes.`
          );
        } else {
          toast.success(`Command "${action}" sent to agent successfully.`);
        }

        // Update audit log
        refreshAuditLog();

        return result;
      } catch (error) {
        console.error(`Error sending command to agent ${agentId}:`, error);
        toast.error(`Failed to send command: ${error.message}`);
        throw error;
      }
    },
    [mqttConnection, userInfo]
  );

  /**
   * Sync changes to backend via REST API
   * @param {string} actionId - Action ID to sync
   * @returns {Promise<Object>} Sync result
   */
  const syncChangesToBackend = useCallback(
    async actionId => {
      if (!pendingChanges.has(actionId)) {
        throw new Error(`No pending changes found for action ID: ${actionId}`);
      }

      const change = pendingChanges.get(actionId);

      try {
        const result = await mqttAdminService.syncChangesToBackend(
          change.agentId,
          change.changes,
          change.actionId,
          userInfo
        );

        // Remove from pending changes
        setPendingChanges(prev => {
          const newMap = new Map(prev);
          newMap.delete(actionId);
          return newMap;
        });

        toast.success('Changes successfully saved to system');

        // Update audit log
        refreshAuditLog();

        return result;
      } catch (error) {
        console.error(`Error syncing changes to backend for action ${actionId}:`, error);
        toast.error(`Failed to save changes to system: ${error.message}`);

        // Update audit log
        refreshAuditLog();

        throw error;
      }
    },
    [pendingChanges, userInfo]
  );

  /**
   * Sync all pending changes to backend
   * @returns {Promise<Array>} Results of all sync operations
   */
  const syncAllChangesToBackend = useCallback(async () => {
    if (pendingChanges.size === 0) {
      toast.info('No pending changes to sync');
      return [];
    }

    const results = [];
    const errors = [];

    // Create a copy of the pending changes to avoid modification during iteration
    const changesToSync = Array.from(pendingChanges.entries());

    for (const [actionId, change] of changesToSync) {
      try {
        const result = await mqttAdminService.syncChangesToBackend(
          change.agentId,
          change.changes,
          change.actionId,
          userInfo
        );

        results.push(result);

        // Remove from pending changes
        setPendingChanges(prev => {
          const newMap = new Map(prev);
          newMap.delete(actionId);
          return newMap;
        });
      } catch (error) {
        console.error(`Error syncing changes for action ${actionId}:`, error);
        errors.push({ actionId, error: error.message });
      }
    }

    // Update audit log
    refreshAuditLog();

    if (errors.length === 0) {
      toast.success('All changes successfully saved to system');
    } else if (results.length > 0) {
      toast.warning(`Saved ${results.length} changes, but ${errors.length} failed`);
    } else {
      toast.error('Failed to save any changes to system');
    }

    return { results, errors };
  }, [pendingChanges, userInfo]);

  /**
   * Monitor agent status via MQTT
   * @param {string} agentId - Agent ID
   * @param {Function} callback - Callback function for status updates
   * @returns {Promise<Function>} Unsubscribe function
   */
  const monitorAgentStatus = useCallback(
    async (agentId, callback) => {
      if (!mqttConnection) {
        throw new Error('MQTT admin connection not initialized');
      }

      return mqttAdminService.monitorAgentStatus(agentId, callback);
    },
    [mqttConnection]
  );

  /**
   * Monitor agent results via MQTT
   * @param {string} agentId - Agent ID
   * @param {Function} callback - Callback function for result messages
   * @returns {Promise<Function>} Unsubscribe function
   */
  const monitorAgentResults = useCallback(
    async (agentId, callback) => {
      if (!mqttConnection) {
        throw new Error('MQTT admin connection not initialized');
      }

      return mqttAdminService.monitorAgentResults(agentId, callback);
    },
    [mqttConnection]
  );

  /**
   * Refresh audit log from MQTT admin service
   */
  const refreshAuditLog = useCallback(() => {
    const log = mqttAdminService.getMqttAuditLog();
    setAuditLog(log);
  }, []);

  /**
   * Get filtered audit log entries
   * @param {Object} filters - Optional filters (agentId, actionType, etc.)
   * @returns {Array<Object>} Filtered audit log entries
   */
  const getFilteredAuditLog = useCallback((filters = {}) => {
    return mqttAdminService.getMqttAuditLog(filters);
  }, []);

  // Initialize connection when component mounts
  useEffect(() => {
    if (['admin', 'tenant_admin'].includes(userRole)) {
      initializeConnection();
    }

    return () => {
      // Clean up logic if needed
    };
  }, [userRole, initializeConnection]);

  // Refresh audit log periodically
  useEffect(() => {
    if (mqttConnection) {
      refreshAuditLog();

      const intervalId = setInterval(refreshAuditLog, 30000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [mqttConnection, refreshAuditLog]);

  return {
    mqttConnection,
    isConnecting,
    connectionError,
    pendingChanges: Array.from(pendingChanges.values()),
    auditLog,
    applyConfigToAgent,
    sendCommandToAgent,
    syncChangesToBackend,
    syncAllChangesToBackend,
    monitorAgentStatus,
    monitorAgentResults,
    getFilteredAuditLog,
    refreshAuditLog,
    canUseMqttAdmin: ['admin', 'tenant_admin'].includes(userRole),
  };
};

export default useMqttAdminConnection;
